/**
 * Order Cancellation Saga
 *
 * Implements the complete order cancellation process using the Saga pattern.
 * Handles the three critical steps: removeOrder, updatePatient, deleteOpa
 * with proper compensation logic for each step.
 */

import { SagaTransaction, createSagaStep, SagaResult } from './SagaTransaction'
import {
  updatePatient,
  deleteOpa,
  getPatients,
  cancelRemoveOrder,
  cancelUpdatePatient,
  cancelDeleteOpa
} from '../ApiService'
import { removeOrderOnce } from '../removeOrderOnce'
import {
  DeleteOrderDto,
  PatientSummarySearchDto
} from '../../types/swaggerTypes'
import {
  PatientOrderStatus,
  OPAOrderTypes
} from '../../pages/Patients/PatientDashboard/Patient.model'

export interface OrderCancellationData {
  // Order data
  orderNumber: string
  orderType: string
  deleteReason: DeleteOrderDto

  // Patient data
  physicianId: string
  patientId?: number
  patientNumber: string
  programId: string

  // Shipping data
  shippingAddressId: string

  // Original patient status for rollback
  originalPatientStatus?: string
}

export interface OrderCancellationResult {
  success: boolean
  orderDeleted: boolean
  patientUpdated: boolean
  opaDeleted: boolean
  error?: Error
  compensationRequired: boolean
}

export class OrderCancellationSaga {
  private logger: (
    level: 'info' | 'warn' | 'error',
    message: string,
    data?: any
  ) => void

  constructor(
    logger?: (
      level: 'info' | 'warn' | 'error',
      message: string,
      data?: any
    ) => void
  ) {
    this.logger =
      logger ||
      (() => {
        // Default logger implementation for development/testing
        // Debug logging removed
      })
  }

  /**
   * Execute the complete order cancellation process
   */
  async cancelOrder(
    data: OrderCancellationData
  ): Promise<SagaResult<OrderCancellationResult>> {
    this.logger('info', 'Starting order cancellation saga', {
      orderNumber: data.orderNumber,
      patientNumber: data.patientNumber,
      orderType: data.orderType
    })

    // Resolve missing data first
    const resolvedData = await this.resolveOrderData(data)

    // Validate required data for deleteOpa
    this.validateDeleteOpaRequirements(resolvedData)

    const saga = new SagaTransaction<OrderCancellationResult>(
      `order_cancellation_${data.orderNumber}_${Date.now()}`,
      this.logger
    )

    // Step 1: Delete Order from EBS
    saga.addStep(
      createSagaStep(
        'delete_order_ebs',
        `Delete order ${resolvedData.orderNumber} from EBS`,
        async () => {
          this.logger('info', 'Executing: Delete order from EBS', {
            orderNumber: resolvedData.orderNumber
          })
          await removeOrderOnce(
            resolvedData.orderNumber,
            resolvedData.deleteReason
          )
          return { orderDeleted: true }
        },
        () => {
          this.logger(
            'warn',
            'Compensation: Delete order from EBS cannot be reversed',
            {
              orderNumber: resolvedData.orderNumber
            }
          )
          // Note: Order deletion from EBS cannot be easily reversed
          // This would require creating a new order or manual intervention
          // We log this for manual review
          cancelRemoveOrder()
          return Promise.resolve()
        }
      )
    )

    // Step 2: Update Patient Status
    saga.addStep(
      createSagaStep(
        'update_patient_status',
        `Update patient ${resolvedData.patientNumber} status`,
        async () => {
          if (!resolvedData.patientId) {
            throw new Error(
              `Patient ID is required for patient ${resolvedData.patientNumber}`
            )
          }

          const newStatus =
            resolvedData.orderType === OPAOrderTypes.Resupply
              ? PatientOrderStatus.ReadyForResupply
              : PatientOrderStatus.BeginOrder

          this.logger('info', 'Executing: Update patient status', {
            patientId: resolvedData.patientId,
            newStatus,
            originalStatus: resolvedData.originalPatientStatus
          })

          await updatePatient(
            resolvedData.physicianId,
            resolvedData.patientId,
            {
              orderStatus: newStatus
            }
          )

          return { patientUpdated: true, newStatus }
        },
        async () => {
          this.logger('info', 'Compensation: Reverting patient status', {
            patientId: resolvedData.patientId,
            originalStatus: resolvedData.originalPatientStatus
          })

          if (resolvedData.patientId && resolvedData.originalPatientStatus) {
            try {
              await updatePatient(
                resolvedData.physicianId,
                resolvedData.patientId,
                {
                  orderStatus: resolvedData.originalPatientStatus
                }
              )
            } catch (error) {
              this.logger('error', 'Failed to revert patient status', { error })
            }
          }
          cancelUpdatePatient()
        }
      )
    )

    // Step 3: Delete OPA from SvC
    saga.addStep(
      createSagaStep(
        'delete_opa_svc',
        `Delete OPA for patient ${resolvedData.patientNumber} from SvC`,
        async () => {
          this.logger('info', 'Executing: Delete OPA from SvC', {
            programId: resolvedData.programId,
            physicianId: resolvedData.physicianId,
            shippingAddressId: resolvedData.shippingAddressId,
            patientNumber: resolvedData.patientNumber
          })

          // Validate parameters before calling deleteOpa
          if (
            !resolvedData.shippingAddressId ||
            resolvedData.shippingAddressId.trim() === ''
          ) {
            throw new Error(
              `CRITICAL: Cannot delete OPA - missing shipping address ID. ` +
                `This will cause cross-system validation issues. ` +
                `Required data: programId=${resolvedData.programId}, ` +
                `physicianId=${resolvedData.physicianId}, ` +
                `patientNumber=${resolvedData.patientNumber}`
            )
          }

          await deleteOpa(
            resolvedData.programId,
            resolvedData.physicianId,
            resolvedData.shippingAddressId,
            resolvedData.patientNumber
          )

          this.logger('info', 'Successfully deleted OPA from SvC', {
            programId: resolvedData.programId,
            patientNumber: resolvedData.patientNumber
          })

          return { opaDeleted: true }
        },
        () => {
          this.logger(
            'warn',
            'Compensation: Delete OPA from SvC cannot be easily reversed',
            {
              patientNumber: resolvedData.patientNumber
            }
          )
          // Note: OPA deletion from SvC cannot be easily reversed
          // This would require recreating the OPA or manual intervention
          // We log this for manual review
          cancelDeleteOpa()
          return Promise.resolve()
        }
      )
    )

    // Execute the saga
    const result = await saga.execute()

    // Prepare the final result
    const cancellationResult: OrderCancellationResult = {
      success: result.success,
      orderDeleted: result.executedSteps.some(
        (step) => step.stepId === 'delete_order_ebs' && !step.error
      ),
      patientUpdated: result.executedSteps.some(
        (step) => step.stepId === 'update_patient_status' && !step.error
      ),
      opaDeleted: result.executedSteps.some(
        (step) => step.stepId === 'delete_opa_svc' && !step.error
      ),
      error: result.error,
      compensationRequired: result.compensatedSteps.length > 0
    }

    this.logger('info', 'Order cancellation saga completed', {
      success: result.success,
      cancellationResult,
      compensatedSteps: result.compensatedSteps,
      totalDuration: result.totalDuration
    })

    return {
      ...result,
      result: cancellationResult
    }
  }

  /**
   * Validate that all required data for deleteOpa is present
   */
  private validateDeleteOpaRequirements(data: OrderCancellationData): void {
    const missingFields: string[] = []

    // Ensure programId is a string and validate
    const programIdStr = String(data.programId || '').trim()
    if (!programIdStr) {
      missingFields.push('programId')
    }

    // Ensure physicianId is a string and validate
    const physicianIdStr = String(data.physicianId || '').trim()
    if (!physicianIdStr) {
      missingFields.push('physicianId')
    }

    // Ensure shippingAddressId is a string and validate
    const shippingAddressIdStr = String(data.shippingAddressId || '').trim()
    if (!shippingAddressIdStr) {
      missingFields.push('shippingAddressId')
    }

    // Ensure patientNumber is a string and validate
    const patientNumberStr = String(data.patientNumber || '').trim()
    if (!patientNumberStr) {
      missingFields.push('patientNumber')
    }

    if (missingFields.length > 0) {
      const errorMessage =
        `CRITICAL: Cannot proceed with order cancellation - missing required data for deleteOpa: ${missingFields.join(
          ', '
        )}. ` +
        `This will cause cross-system validation issues. ` +
        `Order: ${data.orderNumber}, Patient: ${data.patientNumber}`

      this.logger('error', errorMessage, {
        missingFields,
        orderNumber: data.orderNumber,
        patientNumber: data.patientNumber,
        receivedData: {
          programId: data.programId,
          programIdType: typeof data.programId,
          physicianId: data.physicianId,
          physicianIdType: typeof data.physicianId,
          shippingAddressId: data.shippingAddressId,
          shippingAddressIdType: typeof data.shippingAddressId,
          patientNumber: data.patientNumber,
          patientNumberType: typeof data.patientNumber
        }
      })

      throw new Error(errorMessage)
    }

    this.logger('info', 'All required data validated for deleteOpa', {
      programId: programIdStr,
      physicianId: physicianIdStr,
      shippingAddressId: shippingAddressIdStr,
      patientNumber: patientNumberStr
    })
  }

  /**
   * Resolve missing order data by fetching from various sources
   */
  private async resolveOrderData(
    data: OrderCancellationData
  ): Promise<OrderCancellationData> {
    const resolved = { ...data }

    // Try to resolve patient ID if missing
    if (!resolved.patientId && resolved.patientNumber && resolved.physicianId) {
      this.logger('info', 'Resolving missing patient ID', {
        patientNumber: resolved.patientNumber
      })

      try {
        const patientId = await this.getPatientIdFromSearch(
          resolved.patientNumber
        )
        if (patientId) {
          resolved.patientId = patientId
          this.logger('info', 'Patient ID resolved', { patientId })
        }
      } catch (error) {
        this.logger('warn', 'Failed to resolve patient ID', { error })
      }
    }

    // TODO: Add logic to resolve original patient status if needed for compensation
    // This could be done by fetching current patient details before starting the saga

    return resolved
  }

  /**
   * Helper method to get patient ID from patient search
   */
  private async getPatientIdFromSearch(
    patientNumber: string
  ): Promise<number | null> {
    try {
      const searchParams: PatientSummarySearchDto = {
        query: patientNumber,
        filter: {
          programs: [],
          patientOrderStatuses: [],
          showMyPatient: false,
          showMyProgram: false
        },
        pagination: {
          skip: 0,
          take: 1
        },
        sorting: {
          sortBy: 'patientOrderStatus',
          order: 'DESC'
        }
      }

      const response = await getPatients(searchParams)
      const patient = response?.data.result.find(
        (p) => p.patientNumber === patientNumber
      )
      return patient?.patientId || null
    } catch (error) {
      this.logger('error', 'Error fetching patient by number', {
        error,
        patientNumber
      })
      return null
    }
  }

  /**
   * Create a minimal order cancellation data object for simple cases
   */
  static createSimpleOrderCancellation(
    orderNumber: string,
    physicianId: string,
    patientNumber: string,
    programId: string,
    shippingAddressId: string,
    orderType: string = OPAOrderTypes.InitialOrder,
    cancelReason: string = 'User requested cancellation',
    cancelComments: string = ''
  ): OrderCancellationData {
    return {
      orderNumber,
      orderType,
      deleteReason: {
        cancelReason,
        cancelComments
      },
      physicianId,
      patientNumber,
      programId,
      shippingAddressId
    }
  }
}

/**
 * Singleton instance for convenience
 */
export const orderCancellationSaga = new OrderCancellationSaga()
